import React, { useState } from 'react';
import clsx from 'clsx';
import { Info } from 'lucide-react';

import { Trans } from '@lingui/react/macro';

import { Badge } from '../ui/badge';
import { getMetricExplanation, getSeverityColors } from './utils';

interface MetricWithHoverDetailsProps {
  title: string;
  value: any;
  metric: string;
  context: {
    duration?: number;
    wordsPerMinute?: number;
    intent?: string;
    fillerCount?: number;
  };
  className?: string;
  compact?: boolean;
}

export function MetricWithHoverDetails({ 
  title, 
  value, 
  metric, 
  context, 
  className = "",
  compact = false
}: MetricWithHoverDetailsProps) {
  const [isHovered, setIsHovered] = useState(false);
  const explanation = getMetricExplanation(metric, value, context);
  const colors = getSeverityColors(explanation.severity);
  
  if (compact) {
    return (
      <div className="relative flex-1">
        <div 
          className={clsx(
            "flex items-center justify-between cursor-pointer transition-all duration-200 hover:bg-brand-dark-blue/5 rounded px-2 py-1", 
            className
          )}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <span className="text-xs font-medium text-brand-dark-blue/80">{title}:</span>
          <div className="flex items-center gap-1">
            <Badge className={clsx('text-xs font-semibold', colors.badge)}>
              {value}
            </Badge>
            <Info className={clsx("h-3 w-3 transition-transform duration-200", colors.icon, isHovered && "scale-110")} />
          </div>
        </div>

        {/* Compact hover tooltip */}
        {isHovered && (
          <div className={clsx(
            "absolute z-20 top-full left-0 right-0 mt-2 p-4 rounded-lg border-2 bg-white shadow-xl transform transition-all duration-300 ease-out",
            colors.border,
            "animate-in slide-in-from-top-2 fade-in-0"
          )}>
            <h4 className={clsx("text-sm font-bold mb-3 flex items-center gap-2", colors.title)}>
              <Info className="h-4 w-4" />
              <Trans>{title} Analysis</Trans>
            </h4>

            {/* Explanation */}
            <div className={clsx(
              "text-xs p-3 rounded mb-3",
              colors.preview
            )}>
              <div className="flex items-start gap-2">
                <Info className="h-3 w-3 mt-0.5 shrink-0" />
                <span className="font-medium leading-relaxed">{explanation.explanation}</span>
              </div>
            </div>

            {/* Tips */}
            {explanation.tips && explanation.tips.length > 0 && (
              <div className="space-y-2">
                <h5 className={clsx("text-xs font-semibold", colors.title)}>
                  <Trans>💡 Tips for Improvement:</Trans>
                </h5>
                <ul className="space-y-1">
                  {explanation.tips.map((tip: string, tipIndex: number) => (
                    <li key={tipIndex} className="text-xs text-gray-600 flex items-start gap-2">
                      <span className="text-brand-gold font-bold">•</span>
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <div 
        className={clsx(
          "p-4 rounded-lg border-2 bg-gradient-to-br cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02]", 
          colors.border, 
          colors.background, 
          className
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex items-center gap-2 mb-2">
          <h3 className={clsx("text-sm font-bold", colors.title)}>{title}</h3>
          <Info className={clsx("h-4 w-4 transition-transform duration-200", colors.icon, isHovered && "scale-110")} />
        </div>
        
        <Badge className={clsx('font-semibold', colors.badge)}>
          {value}
        </Badge>
      </div>

      {/* Hover tooltip */}
      {isHovered && (
        <div className={clsx(
          "absolute z-10 top-full left-0 right-0 mt-2 p-4 rounded-lg border-2 bg-white shadow-xl transform transition-all duration-300 ease-out",
          colors.border,
          "animate-in slide-in-from-top-2 fade-in-0"
        )}>
          <h4 className={clsx("text-sm font-bold mb-3 flex items-center gap-2", colors.title)}>
            <Info className="h-4 w-4" />
            <Trans>{title} Analysis</Trans>
          </h4>

          {/* Explanation */}
          <div className={clsx(
            "text-xs p-3 rounded mb-3",
            colors.preview
          )}>
            <div className="flex items-start gap-2">
              <Info className="h-3 w-3 mt-0.5 shrink-0" />
              <span className="font-medium leading-relaxed">{explanation.explanation}</span>
            </div>
          </div>

          {/* Tips */}
          {explanation.tips && explanation.tips.length > 0 && (
            <div className="space-y-2">
              <h5 className={clsx("text-xs font-semibold", colors.title)}>
                <Trans>💡 Tips for Improvement:</Trans>
              </h5>
              <ul className="space-y-1">
                {explanation.tips.map((tip: string, tipIndex: number) => (
                  <li key={tipIndex} className="text-xs text-gray-600 flex items-start gap-2">
                    <span className="text-brand-gold font-bold">•</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
